import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import QuickBooksConnectionCard from '../components/QuickBooksConnectionCard';
import gmailService from '../services/gmailService';
import mailService from '../services/mailService';
import { useT } from '../i18n/LanguageContext';
import IntegrationCaveats from '../features/integrations/IntegrationCaveats';
import {
  CheckRow,
  GRAPH_VARS,
  IntegrationCard,
  StatusPill,
} from '../features/integrations/IntegrationPrimitives';

/**
 * Outbound integrations (CL-260824-01).
 *
 * WHY THIS IS ITS OWN PAGE AND NOT A CARD ON /settings: /settings is in REP_ALLOWED_PATHS, so a
 * sales_rep or clinic login reaches it. A new path that is NOT on that whitelist is wrapped in
 * RequireAdmin automatically by App.js - admin-guarded by construction rather than by remembering
 * to add a check. That is the structural guarantee the route table was built for, so this page uses
 * it instead of hand-rolling an isAdmin() branch inside a page reps can open.
 *
 * *** WHAT CHANGED, AND WHY THE PAGE NOW HAS TWO CARDS. ***
 * This page used to be one Gmail card headed "Sends vendor order forms", showing the connected
 * Google address. That stopped being true on 8/31/26: order forms now go out as
 * support@medeffectsllc.com through Microsoft 365, and the page kept naming the old mailbox as the
 * sender. A wrong answer here is worse than no answer - it is the page an admin opens precisely to
 * find out what a vendor sees in the From line.
 *
 * The sender is not something this page can look up from a connection, either. It is decided by
 * MAIL_TRANSPORT on the backend at runtime, so the only correct source is /admin/mail/status, which
 * reports the transport that is actually selected. Hence:
 *
 *   Card 1  ORDER FORMS - the live outbound transport and the address it sends as.
 *   Card 2  GOOGLE MAILBOX - the OAuth grant, described by the job it still does. Under Microsoft
 *           365 that job is READING: BLS shipping notifications arrive at support@medeffectsllc.com
 *           and an Outlook rule redirects them to this mailbox, which the intake poller can read.
 *           The connect flow lives here because re-granting the read scope is the only reason to
 *           touch it (canRead is false on any connection made before 9/1/26).
 *
 * NOTHING HERE SENDS MAIL. Connecting a mailbox only makes sending possible; every actual send is
 * still refused unless VENDOR_EMAIL_ENABLED is the exact string "true" and an admin passes
 * confirm:true after reading a preview. See vendor-email.service.ts for the full six-gate list.
 */

export default function Integrations() {
  const t = useT();
  const [mail, setMail] = useState(null);
  const [mailLoading, setMailLoading] = useState(true);
  const [mailError, setMailError] = useState('');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [intakeHealth, setIntakeHealth] = useState(null);
  const [intakeHealthError, setIntakeHealthError] = useState(false);

  const [link, setLink] = useState(null);
  const [minting, setMinting] = useState(false);
  const [mintError, setMintError] = useState('');
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const fetchMail = useCallback(async () => {
    setMailError('');
    try {
      const res = await mailService.getStatus();
      setMail(res.data?.data || null);
    } catch {
      setMailError('Could not read the outbound mail status.');
    } finally {
      setMailLoading(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoadError('');
    try {
      const res = await gmailService.getStatus();
      setStatus(res.data?.data || null);
      return res.data?.data || null;
    } catch {
      setLoadError('Could not read the Google connection status.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIntakeHealth = useCallback(async () => {
    try {
      const res = await gmailService.getIntakeStatus();
      setIntakeHealth(res.data?.data || null);
      setIntakeHealthError(false);
    } catch {
      setIntakeHealth(null);
      setIntakeHealthError(true);
    }
  }, []);

  // Independent reads, and independently reported. One card failing must not blank the other: they
  // answer different questions, and "which address sends" is still worth having when the Google
  // read fails, and vice versa.
  useEffect(() => {
    fetchMail();
    fetchStatus();
    fetchIntakeHealth();
  }, [fetchMail, fetchStatus, fetchIntakeHealth]);

  // Drives the expiry countdown. Only runs while a link is actually on screen.
  useEffect(() => {
    if (!link) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [link]);

  const secondsLeft = useMemo(() => {
    if (!link?.expiresAt) return 0;
    return Math.max(0, Math.floor((new Date(link.expiresAt).getTime() - now) / 1000));
  }, [link, now]);

  const expired = !!link && secondsLeft <= 0;

  const handleConnect = async () => {
    setMinting(true);
    setMintError('');
    setCopied(false);
    try {
      const res = await gmailService.beginConnect();
      const data = res.data?.data;
      if (!data?.authorizeUrl) throw new Error('The server returned no authorization link.');
      setNow(Date.now());
      setLink(data);
    } catch {
      setMintError('Could not create an authorization link.');
    } finally {
      setMinting(false);
    }
  };

  // Re-read after the consent screen is completed in the other tab. Clears the link on success so
  // a used, dead nonce is not left sitting on screen looking actionable.
  const handleRecheck = async () => {
    setLoading(true);
    setMailLoading(true);
    fetchMail();
    fetchIntakeHealth();
    const fresh = await fetchStatus();
    if (fresh?.connected) setLink(null);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link.authorizeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (permissions, insecure origin). The URL is rendered in a
      // readonly input for exactly this case, so select it rather than failing silently.
      const el = document.getElementById('gmail-authorize-url');
      if (el) {
        el.focus();
        el.select();
      }
    }
  };

  const configured = !!status?.configured;
  const connected = !!status?.connected;
  const canRead = !!status?.canRead;

  // Tri-state on purpose. "Not graph" and "we could not read which transport is live" are different
  // facts, and collapsing them is how this page came to name the wrong sender in the first place -
  // it assumed the connection it could see was the one that sends.
  const transportKnown = !!mail?.transport;
  const graph = mail?.transport === 'graph';
  // Under Gmail the mail status IS the Gmail status, so the address lives under a different key.
  const sendingAs = graph ? mail?.sender : transportKnown ? mail?.connectedAs || null : null;
  const transportLabel = mail?.label || (graph ? 'Microsoft 365' : 'Gmail');
  /** An address AND a transport ready to use it. Either half alone sends nothing. */
  const canSend = !!sendingAs && (graph ? !!mail?.configured : !!mail?.connected);

  // Graph has no "connected" state to report: there is no grant to complete, only four variables
  // that are either set or not. Saying "Connected" here would claim more than the backend knows.
  const sendTone = graph
    ? mail?.configured
      ? 'good'
      : 'bad'
    : connected
      ? 'good'
      : configured
        ? 'pending'
        : 'bad';
  const sendToneLabel = graph
    ? mail?.configured
      ? t('Configured')
      : t('Not configured')
    : connected
      ? t('Connected')
      : configured
        ? t('Configured - not connected')
        : t('Not configured');

  // `missing` is authoritative when present. The fallback covers a backend that predates it, where
  // the only signal is the single `configured` boolean.
  const graphMissing = Array.isArray(mail?.missing) ? mail.missing : [];
  const graphVarOk = (name) =>
    graphMissing.length ? !graphMissing.includes(name) : !!mail?.configured;

  // What the Google grant is actually for, which depends on what is sending. Under Microsoft 365 it
  // reads and does not send - and saying so is the correction this page needed. With the transport
  // unknown it claims neither, rather than guessing the answer that was wrong before.
  const googleRole = !transportKnown
    ? t('The Google OAuth grant. What it is used for depends on the outbound transport above.')
    : graph
      ? t(
          'Reads the BLS shipping notifications that arrive at {mailbox} and are redirected here. It does not send anything.',
          { mailbox: sendingAs || t('the sending mailbox') }
        )
      : t('Sends vendor order forms, and reads BLS shipping notifications.');

  const googleTone = !configured
    ? 'bad'
    : !connected
      ? 'pending'
      : graph && !canRead
        ? 'pending'
        : 'good';
  const googleToneLabel = !configured
    ? t('Not configured')
    : !connected
      ? t('Configured - not connected')
      : graph && !canRead
        ? t('No read permission')
        : t('Connected');

  return (
    <div>
      <PageHeader
        title={t('Integrations')}
        subtitle={t('Outbound connections this deployment holds on your behalf.')}
      />

      <QuickBooksConnectionCard />

      {/* ---- Card 1: what actually sends the order forms ------------------------------------- */}
      <IntegrationCard
        icon="outgoing_mail"
        name={
          transportKnown
            ? t('Order forms - {transport}', { transport: transportLabel })
            : t('Order forms')
        }
        role={t('The mailbox vendors see in the From line, and where their replies land.')}
        pill={
          !mailLoading && !mailError && mail ? (
            <StatusPill tone={sendTone}>{sendToneLabel}</StatusPill>
          ) : null
        }
      >
        {mailLoading && (
          <p className="text-[13px] text-[#64748b] py-4">{t('Reading outbound mail status...')}</p>
        )}

        {!mailLoading && mailError && (
          <div className="p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600">
            {t(mailError)}
          </div>
        )}

        {!mailLoading && !mailError && mail && (
          <>
            {/*
              GREEN MEANS THIS ADDRESS CAN ACTUALLY SEND, which is not the same as an address being
              configured. GRAPH_SENDER can be set while the client secret is missing - the sender is
              then a statement of intent, and painting it green would say the send path is fine when
              it is broken.
            */}
            <div
              className={`p-4 rounded-[10px] mb-5 border ${
                canSend ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
              }`}
            >
              <p
                className={`text-[12px] font-semibold uppercase tracking-wider mb-2 ${
                  canSend ? 'text-green-700' : 'text-amber-800'
                }`}
              >
                {t('Sending as')}
              </p>
              {sendingAs ? (
                <p className="text-[15px] font-semibold text-[#0f172a] font-mono break-all">
                  {sendingAs}
                </p>
              ) : (
                <p className="text-[13px] text-amber-800">
                  {t('No mailbox is available on the {transport} transport.', {
                    transport: transportLabel,
                  })}
                </p>
              )}
              <p className="text-[12px] text-[#64748b] mt-1">
                {t('Transport: {transport} (MAIL_TRANSPORT={value})', {
                  transport: transportLabel,
                  value: mail.transport,
                })}
              </p>
              {!canSend && (
                <p className="text-[13px] text-amber-800 mt-2">
                  {t('An order form sent now would be refused - see what is missing below.')}
                </p>
              )}
            </div>

            {graph && (
              <>
                <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-1">
                  {t('Deployment configuration')}
                </p>
                <div className="divide-y divide-[#f1f5f9] mb-5">
                  {GRAPH_VARS.map((v) => (
                    <CheckRow
                      key={v.name}
                      ok={graphVarOk(v.name)}
                      label={v.name}
                      value={v.name === 'GRAPH_SENDER' ? mail.sender || t('Set') : t('Set')}
                      missingHint={t(v.missingHint)}
                    />
                  ))}
                </div>

                {/*
                  THE ONE THING THIS CARD CANNOT TELL YOU, stated where the green pill is, not in a
                  footnote. status() reads four environment variables and never contacts Microsoft,
                  so an expired secret is indistinguishable from a healthy one here.
                */}
                <div className="p-3 rounded-lg text-[13px] bg-[#f8fafc] border border-[#e2e8f0] text-[#334155]">
                  <span className="font-semibold text-[#0f172a]">
                    {t('Configured is not the same as working.')}
                  </span>{' '}
                  {t(
                    'These four values are present, which is all this check knows - it never contacts Microsoft. An expired client secret reports exactly the same state and fails on the first send. To prove the credential, POST /admin/mail/test with a recipient; it sends one diagnostic message and contacts no vendor.'
                  )}
                </div>
              </>
            )}
          </>
        )}
      </IntegrationCard>

      {/* ---- Card 2: the Google grant, described by the job it still does -------------------- */}
      <IntegrationCard
        icon="mail"
        name={t('Google mailbox')}
        role={googleRole}
        pill={
          !loading && !loadError && status ? (
            <StatusPill tone={googleTone}>{googleToneLabel}</StatusPill>
          ) : null
        }
      >
        {loading && (
          <p className="text-[13px] text-[#64748b] py-4">{t('Reading connection status...')}</p>
        )}

        {!loading && loadError && (
          <div className="p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600 mb-4">
            {t(loadError)}
          </div>
        )}

        {!loading && !loadError && status && (
          <>
            {/* Connected mailbox */}
            {connected && (
              <div className="p-4 rounded-[10px] bg-[#f8fafc] border border-[#e2e8f0] mb-5">
                <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-2">
                  {t('Authorized mailbox')}
                </p>
                <p className="text-[15px] font-semibold text-[#0f172a] font-mono break-all">
                  {status.connectedAs}
                </p>
                {status.connectedAt && (
                  <p className="text-[12px] text-[#64748b] mt-1">
                    {t('Authorized {when}', {
                      when: new Date(status.connectedAt).toLocaleString(),
                    })}
                  </p>
                )}
                {Array.isArray(status.scopes) && status.scopes.length > 0 && (
                  <p className="text-[12px] text-[#64748b] mt-2 break-all">
                    {t('Granted: {scopes}', { scopes: status.scopes.join(', ') })}
                  </p>
                )}
              </div>
            )}

            {connected &&
              (intakeHealthError ||
                intakeHealth?.health?.findings?.some((f) =>
                  ['document_source_stale', 'graph_pdf_unavailable'].includes(f.key)
                )) && (
                <div
                  role="alert"
                  className="p-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-900 mb-5"
                >
                  {t(
                    intakeHealthError
                      ? 'Shipping PDF intake status is unavailable. Check the backend connection.'
                      : 'Shipping PDFs may not be reaching the app. Check the BLS emails in support Outlook and the intake mailbox delivery path.'
                  )}
                </div>
              )}

            {/*
              READ PERMISSION IS THE ONE THAT SILENTLY MATTERS NOW. Any grant made before 9/1/26
              carries gmail.send and nothing else, so the intake poller cannot list a single message
              - and the connection looks perfectly healthy while tracking numbers stop arriving.
            */}
            {graph && connected && !canRead && (
              <div className="p-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-800 mb-5">
                {t(
                  'This connection has no read permission, so shipping notifications cannot be read and tracking numbers will not appear on orders. It was authorized before reading was needed. Connect again below - the consent screen now includes Read.'
                )}
              </div>
            )}

            {/* Deployment prerequisites */}
            <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-1">
              {t('Deployment configuration')}
            </p>
            <div className="divide-y divide-[#f1f5f9] mb-5">
              <CheckRow
                ok={!!status.clientIdSet}
                label="GMAIL_CLIENT_ID"
                value={t('Set')}
                missingHint={t(
                  'Not set in Render. Add it from the Google Cloud OAuth client, then redeploy.'
                )}
              />
              <CheckRow
                ok={!!status.clientSecretSet}
                label="GMAIL_CLIENT_SECRET"
                value={t('Set')}
                missingHint={t(
                  'Not set in Render. Shown only once when the OAuth client is created.'
                )}
              />
              <CheckRow
                ok={!!status.redirectUri}
                label="GMAIL_REDIRECT_URI"
                value={status.redirectUri}
                missingHint={t(
                  'Not set in Render. Must match an Authorized redirect URI on the OAuth client exactly.'
                )}
              />
            </div>

            {!configured && (
              <div className="p-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-800 mb-5">
                {t(
                  'Connecting is blocked until all three are set on the backend. They are read at startup, so finish the Render changes and let the service redeploy first.'
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleConnect}
                disabled={!configured || minting}
                className="h-[42px] px-6 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {minting
                  ? t('Creating link...')
                  : connected
                    ? t('Reconnect a different mailbox')
                    : t('Connect Google mailbox')}
              </button>
              <button
                onClick={handleRecheck}
                className="h-[42px] px-5 border border-[#d6dce8] text-[#24315d] text-sm font-semibold rounded-[10px] hover:bg-[#f8fafc] transition-colors"
              >
                {t('Check connection')}
              </button>
            </div>

            {mintError && (
              <div className="mt-4 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600">
                {t(mintError)}
              </div>
            )}

            {/* The minted consent link */}
            {link && (
              <div className="mt-5 p-4 rounded-[10px] border border-[#d6dce8] bg-[#f8fafc]">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider">
                    {t('Authorization link')}
                  </p>
                  {expired ? (
                    <StatusPill tone="bad">{t('Expired')}</StatusPill>
                  ) : (
                    <StatusPill tone="pending">
                      {t('Expires in {time}', {
                        time: `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`,
                      })}
                    </StatusPill>
                  )}
                </div>

                {expired ? (
                  <p className="text-[13px] text-[#64748b]">
                    {t(
                      'This link has passed its 10 minute limit and will be rejected. Click Connect Google mailbox again for a fresh one.'
                    )}
                  </p>
                ) : (
                  <>
                    <p className="text-[13px] text-[#64748b] mb-3">
                      {t(
                        'Single use, 10 minute limit. It carries no MedEffects credential, so it is safe to send to whoever holds the mailbox password - they sign in, not you.'
                      )}
                    </p>

                    <input
                      id="gmail-authorize-url"
                      readOnly
                      value={link.authorizeUrl}
                      onFocus={(e) => e.target.select()}
                      className="w-full h-[42px] px-3 border border-[#d6dce8] rounded-[8px] text-[12px] text-[#24315d] font-mono bg-white mb-3"
                    />

                    <div className="flex flex-wrap items-center gap-3">
                      <a
                        href={link.authorizeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-[42px] px-5 inline-flex items-center gap-2 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors"
                      >
                        <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                        {t('Open Google consent screen')}
                      </a>
                      <button
                        onClick={handleCopy}
                        className="h-[42px] px-5 inline-flex items-center gap-2 border border-[#d6dce8] text-[#24315d] text-sm font-semibold rounded-[10px] hover:bg-white transition-colors"
                      >
                        <span className="material-symbols-outlined text-[18px]">content_copy</span>
                        {copied ? t('Copied') : t('Copy link')}
                      </button>
                    </div>

                    <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                      <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-2">
                        {t('On the Google screen')}
                      </p>
                      <ul className="text-[13px] text-[#64748b] space-y-1.5 list-disc pl-5">
                        <li>
                          {t(
                            'Sign in as the mailbox you want to connect, and nothing else. Whichever account signs in is the one that gets connected.'
                          )}
                        </li>
                        <li>
                          {t(
                            '"Google hasn\'t verified this app" is expected for an internal tool. Choose Advanced, then continue.'
                          )}
                        </li>
                        <li>
                          {t(
                            'Leave every checkbox ticked, Read included. If Read is unticked, shipping notifications cannot be read and tracking numbers stop appearing.'
                          )}
                        </li>
                        <li>{t('Come back here and choose Check connection.')}</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </IntegrationCard>

      <IntegrationCaveats graph={graph} transportKnown={transportKnown} sendingAs={sendingAs} />
    </div>
  );
}
